/**
 * `/defenses` + `/defenses/:id` — phiên bảo vệ khoá luận từ xa (TT 83 C10).
 * Đồng ý ghi hình riêng → (người bảo vệ) đối chiếu khuôn mặt → vào phòng. Chủ tịch bắt đầu /
 * kết thúc / ghi lại khi lỗi; chủ tịch/thư ký ghi kết quả; hội đồng xem biên bản.
 */
import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Input,
  List,
  Result,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import {
  DEFENSE_CONSENT_POINTS,
  type Defense,
  RESULT_LABEL,
  ROLE_LABEL,
  endDefense,
  getDefense,
  getDefenseMinutes,
  giveDefenseConsent,
  joinDefense,
  listDefenses,
  restartDefenseRecording,
  saveDefenseResult,
  startDefense,
  submitDefenseFaceMatch,
} from "@/features/defense/api";
import { ImageCapture } from "@/features/identity/ImageCapture";
import { LiveClassRoom } from "@/features/live/LiveClassRoom";
import { formatDateTime } from "@/lib/formatDate";

const { Title, Text, Paragraph } = Typography;

type ApiErr = { response?: { data?: { detail?: string; code?: string } } };
const detail = (e: unknown, fb: string) => (e as ApiErr).response?.data?.detail ?? fb;

export function DefenseRoom() {
  const { id } = useParams();
  return id ? <DefenseDetail id={Number(id)} /> : <DefenseList />;
}

function DefenseList() {
  const q = useQuery({ queryKey: ["defenses"], queryFn: listDefenses });
  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Title level={2}>Phiên bảo vệ khoá luận / đồ án</Title>
      <Card>
        <List
          loading={q.isLoading}
          dataSource={q.data?.results ?? []}
          locale={{ emptyText: "Không có phiên bảo vệ nào." }}
          renderItem={(d) => (
            <List.Item extra={<Link to={`/defenses/${d.id}`}>Mở</Link>}>
              <List.Item.Meta
                title={d.thesis_title}
                description={`${d.student.name} · ${formatDateTime(d.scheduled_start)} · ${
                  d.my_role ? ROLE_LABEL[d.my_role] : "Quản trị"
                }`}
              />
            </List.Item>
          )}
        />
      </Card>
    </div>
  );
}

function ConsentCard({ d }: { d: Defense }) {
  const qc = useQueryClient();
  const [ok, setOk] = useState(false);
  const mut = useMutation({
    mutationFn: () => giveDefenseConsent(d.id),
    onSuccess: (data) => qc.setQueryData(["defense", d.id], data),
    onError: (e) => message.error(detail(e, "Không ghi nhận được")),
  });
  return (
    <Card title="Đồng ý ghi hình, ghi âm phiên bảo vệ">
      <ul>
        {DEFENSE_CONSENT_POINTS.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      <Checkbox checked={ok} onChange={(e) => setOk(e.target.checked)}>
        Tôi đã đọc và đồng ý
      </Checkbox>
      <div style={{ marginTop: 12 }}>
        <Button type="primary" disabled={!ok} loading={mut.isPending} onClick={() => mut.mutate()}>
          Xác nhận đồng ý
        </Button>
      </div>
    </Card>
  );
}

function FaceMatchCard({ d, onPassed }: { d: Defense; onPassed: () => void }) {
  const [selfie, setSelfie] = useState<File | null>(null);
  const mut = useMutation({
    mutationFn: () => submitDefenseFaceMatch(d.id, selfie!),
    onSuccess: (r) => {
      if (r.status === "passed") onPassed();
      else if (r.status === "needs_review") message.info("Ảnh đang chờ duyệt.");
      else message.warning(r.reason || "Không khớp — chụp lại rõ mặt.");
    },
    onError: (e) => message.error(detail(e, "Không gửi được ảnh")),
  });
  return (
    <Card title="Đối chiếu khuôn mặt người bảo vệ">
      <Paragraph>Chụp ảnh chân dung để đối chiếu với ảnh xác thực danh tính đã đăng ký.</Paragraph>
      <ImageCapture
        label="Ảnh chân dung"
        value={selfie}
        onChange={setSelfie}
        facing="user"
        testId="defense-selfie"
      />
      <Button
        type="primary"
        style={{ marginTop: 12 }}
        disabled={!selfie}
        loading={mut.isPending}
        onClick={() => mut.mutate()}
      >
        Gửi đối chiếu
      </Button>
    </Card>
  );
}

function ResultForm({ d }: { d: Defense }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<string>(d.result || "dat");
  const [note, setNote] = useState(d.minutes_note);
  const mut = useMutation({
    mutationFn: () => saveDefenseResult(d.id, { result, minutes_note: note }),
    onSuccess: (data) => {
      message.success("Đã lưu kết quả");
      qc.setQueryData(["defense", d.id], data);
    },
    onError: (e) => message.error(detail(e, "Không lưu được")),
  });
  return (
    <Card title="Kết quả phiên bảo vệ">
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          value={result}
          onChange={setResult}
          style={{ width: 200 }}
          options={[
            { value: "dat", label: "Đạt" },
            { value: "khong_dat", label: "Không đạt" },
          ]}
        />
        <Input.TextArea
          rows={4}
          placeholder="Ghi chú biên bản"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button type="primary" loading={mut.isPending} onClick={() => mut.mutate()}>
          Lưu kết quả
        </Button>
      </Space>
    </Card>
  );
}

function Minutes({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["defense-minutes", id], queryFn: () => getDefenseMinutes(id) });
  const m = q.data;
  if (!m) return <Spin />;
  return (
    <Card title="Biên bản phiên bảo vệ" data-testid="defense-minutes">
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Cơ sở đào tạo">{m.tenant_name}</Descriptions.Item>
        <Descriptions.Item label="Đề tài">{m.thesis_title}</Descriptions.Item>
        <Descriptions.Item label="Người bảo vệ">
          {m.student.name} ({m.student.student_id || "—"})
        </Descriptions.Item>
        <Descriptions.Item label="Hội đồng">
          {m.committee.map((c) => `${ROLE_LABEL[c.role]}: ${c.name}`).join("; ")}
        </Descriptions.Item>
        <Descriptions.Item label="Thời gian">
          {formatDateTime(m.started_at)} → {formatDateTime(m.ended_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Kết quả">
          {RESULT_LABEL[m.result]} {m.result_recorded_by && `(ghi bởi ${m.result_recorded_by})`}
        </Descriptions.Item>
        <Descriptions.Item label="Ghi chú">{m.minutes_note || "—"}</Descriptions.Item>
        <Descriptions.Item label="Bản ghi hình">
          {m.recording_valid ? (
            <Tag color="green">Đầy đủ</Tag>
          ) : (
            <Tag color="red">{m.recording_invalid_reason}</Tag>
          )}
          {m.recordings.map((r) => (
            <div key={r.id}>
              #{r.id} · {r.status} · SHA-256 <Text code>{r.sha256 || "chưa có"}</Text> · lưu đến{" "}
              {r.retention_until ?? "—"}
            </div>
          ))}
        </Descriptions.Item>
      </Descriptions>
      <Text type="secondary">Lập lúc {formatDateTime(m.generated_at)}</Text>
    </Card>
  );
}

function DefenseDetail({ id }: { id: number }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["defense", id], queryFn: () => getDefense(id) });
  const [gateCode, setGateCode] = useState<string | null>(null);
  const join = useMutation({
    mutationFn: () => joinDefense(q.data!.live_class_id),
    onSuccess: () => setGateCode(null),
    onError: (e) => {
      const code = (e as ApiErr).response?.data?.code ?? null;
      setGateCode(code);
      if (code !== "face_match_required") message.error(detail(e, "Không vào được phòng"));
    },
  });
  const control = useMutation({
    mutationFn: (action: "start" | "end" | "restart") => {
      const lc = q.data!.live_class_id;
      return action === "start"
        ? startDefense(lc)
        : action === "end"
          ? endDefense(lc)
          : restartDefenseRecording(lc);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["defense", id] }),
    onError: (e) => message.error(detail(e, "Thao tác không thành công")),
  });

  if (q.isLoading) return <Spin />;
  const d = q.data;
  if (!d) return <Result status="404" title="Không tìm thấy phiên bảo vệ" />;
  if (join.data) {
    return (
      <LiveClassRoom
        serverUrl={join.data.url}
        token={join.data.token}
        liveClass={{
          title: `Bảo vệ: ${d.thesis_title}`,
          course_name: d.course_code,
          // Bảo vệ khoá luận luôn ghi hình bắt buộc (TT 56 Đ12.2).
          recording_enabled: true,
          started_at: d.started_at,
        }}
        onDisconnected={() => join.reset()}
      />
    );
  }
  const isChair = d.my_role === "chu_tich";
  const canRecordResult = d.my_role === "chu_tich" || d.my_role === "thu_ky";
  const isCommittee = !!d.my_role && d.my_role !== "student";

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Card>
          <Title level={3} style={{ marginTop: 0 }}>
            {d.thesis_title}
          </Title>
          <Text>
            {d.student.name} · {d.course_code} · {formatDateTime(d.scheduled_start)} ·{" "}
            {d.my_role ? ROLE_LABEL[d.my_role] : "Quản trị"}
          </Text>
          {d.status === "live" && !d.recording_active && (
            <Alert
              style={{ marginTop: 12 }}
              type="error"
              showIcon
              message="Ghi hình đang KHÔNG chạy — phiên chưa có bản ghi hoàn chỉnh."
              description={isChair ? "Chủ tịch bấm “Ghi hình lại” để tiếp tục ghi." : undefined}
            />
          )}
          {isChair && (
            <Space style={{ marginTop: 12 }}>
              {d.status === "scheduled" && (
                <Button type="primary" onClick={() => control.mutate("start")}>
                  Bắt đầu phiên (tự ghi hình)
                </Button>
              )}
              {d.status === "live" && !d.recording_active && (
                <Button danger onClick={() => control.mutate("restart")}>
                  Ghi hình lại
                </Button>
              )}
              {d.status === "live" && (
                <Button onClick={() => control.mutate("end")}>Kết thúc phiên</Button>
              )}
            </Space>
          )}
        </Card>
        {d.my_role && d.status !== "ended" && !d.consent_given && <ConsentCard d={d} />}
        {d.my_role === "student" && gateCode === "face_match_required" && (
          <FaceMatchCard d={d} onPassed={() => join.mutate()} />
        )}
        {gateCode === "ekyc_required" && (
          <Alert
            type="warning"
            message="Cần hoàn tất xác thực danh tính trước khi bảo vệ."
            action={<Link to="/identity-verify">Xác thực</Link>}
          />
        )}
        {d.my_role && d.consent_given && d.status !== "ended" && (
          <Button
            type="primary"
            size="large"
            loading={join.isPending}
            onClick={() => join.mutate()}
          >
            Vào phòng bảo vệ
          </Button>
        )}
        {d.status === "ended" && canRecordResult && <ResultForm d={d} />}
        {(isCommittee || d.my_role === null) && d.status === "ended" && <Minutes id={d.id} />}
      </Space>
    </div>
  );
}
